import { useEffect, useState } from 'react'
import axios from 'axios'

export interface BriefIdentity {
  teamID: string
  customerID: string
  briefType: string
  errorFallback?: string
}

export interface BriefFetchState<T> {
  briefing: T | null
  loading: boolean
  error: string | null
}

interface IdentityState<T> extends BriefFetchState<T> {
  identityKey: string
}

const identityKeyOf = ({ teamID, customerID, briefType }: BriefIdentity): string =>
  JSON.stringify([teamID, customerID, briefType])

/** Fetches a brief for exactly one team/customer/type identity. */
export const useBriefData = <T>({
  teamID,
  customerID,
  briefType,
  errorFallback = 'An error occurred',
}: BriefIdentity): BriefFetchState<T> => {
  const identityKey = identityKeyOf({ teamID, customerID, briefType })
  const [state, setState] = useState<IdentityState<T>>({
    identityKey,
    briefing: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    const controller = new AbortController()
    let current = true

    setState({ identityKey, briefing: null, loading: true, error: null })
    axios
      .post<T>(`/api/teams/${teamID}/customers/${customerID}/briefs/${briefType}/generate`, null, {
        signal: controller.signal,
      })
      .then(response => {
        if (current) setState({ identityKey, briefing: response.data, loading: false, error: null })
      })
      .catch(err => {
        if (current && !axios.isCancel(err)) {
          setState({ identityKey, briefing: null, loading: false, error: err.message || errorFallback })
        }
      })

    return () => {
      current = false
      controller.abort()
    }
  }, [teamID, customerID, briefType, identityKey, errorFallback])

  // Hide the previous identity synchronously, before the replacement effect runs.
  if (state.identityKey !== identityKey) return { briefing: null, loading: true, error: null }
  return state
}
